import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiArrowLeft, FiUser, FiMail, FiLock, FiBriefcase, FiMapPin, FiCpu } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import { API_BASE_URL } from "../utils/apiConfig";

function AddUser() {
    const navigate = useNavigate();
    const [devices, setDevices] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        role: "admin",
        organization_name: "",
        location: "",
        device_id: ""
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const loggedInUserRole = localStorage.getItem("role");

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate("/");
            return;
        }

        if (loggedInUserRole !== 'superadmin') {
            navigate("/homePage");
            return;
        }

        const fetchDevices = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${API_BASE_URL}/api/devices/all`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setDevices(response.data.data);
            } catch (err) {
                console.error("Failed to fetch devices", err);
            }
        };
        fetchDevices();
    }, [loggedInUserRole, navigate]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: "", type: "" });

        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${API_BASE_URL}/api/admins`, formData, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.status) {
                setMessage({ text: response.data.message || "User created successfully!", type: "success" });
                setTimeout(() => navigate("/usersPage"), 2000);
            } else {
                setMessage({ text: response.data.message || "Failed to create user", type: "error" });
            }
        } catch (err) {
            setMessage({
                text: err.response?.data?.message || "Server error occurred",
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="w-full mt-8">
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => navigate("/usersPage")}
                        className="flex items-center gap-2 text-gray-400 hover:text-green-400 font-semibold transition py-2 px-4 rounded-xl hover:bg-gray-900 border border-transparent hover:border-gray-800"
                    >
                        <FiArrowLeft /> Back to Dashboard
                    </button>
                    <h2 className="text-3xl font-black text-white tracking-tight">Create <span className="text-green-400">Personnel</span> Account</h2>
                </div>

                <div className="bg-[#1b1d1a] rounded-3xl p-10 border border-gray-800 shadow-2xl relative overflow-hidden">
                    {/* Decorative background element */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-green-400/5 blur-[100px] -mr-32 -mt-32 rounded-full" />

                    <div className="relative z-10">
                        <div className="mb-10">
                            <h3 className="text-xl font-bold text-green-400">Account Credentials</h3>
                            <p className="text-gray-500 text-sm mt-1">Fill in the essential identity and access details for the new user.</p>
                        </div>

                        {message.text && (
                            <div className={`p-4 rounded-2xl mb-8 text-center text-sm font-bold border animate-in fade-in slide-in-from-top-4 duration-300 ${message.type === "success"
                                ? "bg-green-950/20 text-green-400 border-green-800/50"
                                : "bg-red-950/20 text-red-400 border-red-800/50"
                                }`}>
                                {message.text}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                                {/* Full Name */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">Full Legal Name</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FiUser className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            placeholder="John Doe"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">Email Address</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FiMail className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            placeholder="john@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">Security Password</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FiLock className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <input
                                            type="password"
                                            name="password"
                                            required
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>

                                {/* Role */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">User Permissions</label>
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all appearance-none cursor-pointer font-bold text-green-400 shadow-inner"
                                    >
                                        {loggedInUserRole === 'superadmin' && (
                                            <option value="admin">System Administrator</option>
                                        )}
                                        <option value="technician">Field Technician</option>
                                    </select>
                                </div>

                                {/* Organization */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">Organization / Brand</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FiBriefcase className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <input
                                            type="text"
                                            name="organization_name"
                                            required
                                            placeholder="e.g. FitHouse Hub"
                                            value={formData.organization_name}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>

                                {/* Location */}
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-400 ml-1">Assigned Location</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <FiMapPin className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <input
                                            type="text"
                                            name="location"
                                            required
                                            placeholder="City, State"
                                            value={formData.location}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all placeholder:text-gray-700"
                                        />
                                    </div>
                                </div>

                                {/* Device ID */}
                                <div className="lg:col-span-3 space-y-4 pt-4 border-t border-gray-800">
                                    <div>
                                        <h3 className="text-lg font-bold text-green-400">Hardware Association</h3>
                                        <p className="text-gray-500 text-sm mt-1">Select the specific vending unit this user will monitor and manage.</p>
                                    </div>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                                            <FiCpu className="text-gray-500 group-focus-within:text-green-400 transition-colors" />
                                        </div>
                                        <select
                                            name="device_id"
                                            required
                                            value={formData.device_id}
                                            onChange={handleChange}
                                            className="w-full bg-black/50 border border-gray-800 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-green-400/50 focus:border-green-400 outline-none transition-all cursor-pointer appearance-none shadow-inner"
                                        >
                                            <option value="">Choose Vending Machine from Fleet...</option>
                                            {devices.map((device) => (
                                                <option key={device.device_id_PK} value={device.device_id_PK}>
                                                    {device.device_name} — {device.location} (ID: {device.device_id_PK})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full py-5 rounded-2xl text-black font-black text-xl bg-green-400 hover:bg-green-500 transition-all shadow-2xl shadow-green-400/20 active:scale-[0.98] flex items-center justify-center gap-3 ${loading ? "opacity-70 cursor-wait" : "hover:-translate-y-1"
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-5 h-5 border-4 border-black border-t-transparent rounded-full animate-spin" />
                                            Initializing Secure Profile...
                                        </>
                                    ) : "Authorize & Create Account"}
                                </button>
                                <p className="text-center text-gray-600 text-[11px] mt-4 uppercase tracking-[0.2em] font-bold">Admin verification required for final activation</p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddUser;
